import { isArray, isEmpty } from "lodash";
import { DateTime } from "luxon";
import { GetterTree, ActionTree, MutationTree } from "vuex";
import Vue from "vue";
import { RatePlan } from "@/components/calendar/CalendarTypes";
import { ListModel } from "@/shared/models/listModel";
import * as _ from 'lodash';
import { ContingentDataRequest, 
  ContingentDaysDataView, 
  ContingentDemandDataView, 
  ContingentMonthView, 
  ContingentRatePlanDataView, 
  ContingentRatePlanView, 
  ContingentUnitTypeView 
} from "@/components/contingent/ContingentTypes";

export type ContingentDateSpans = "week" | "month" | "year";
export type ContingentDataFormat = "number" | "percentage";
export type ContingentSettings = {
  dateSpan: ContingentDateSpans;
  dataFormat: ContingentDataFormat;
  showPrice: boolean;
  showPriceDifference: boolean;
  selectedPropertyId: number;
  selectedRatePlanId: number | null;
  userId: number;
  tenantId: number;
};

export const defaultSettings = {
  dateSpan: "month",
  dataFormat: "percentage",
  showPrice: false,
  showPriceDifference: false,
  selectedPropertyId: 0,
  selectedRatePlanId: null,
  userId: 0,
  tenantId: 0,
} as ContingentSettings;

let _settings: ContingentSettings[] = [defaultSettings];
const saved = localStorage.getItem("contingent.settings");
if (saved) {
  try {
    _settings = JSON.parse(saved) as ContingentSettings[];
  } catch {
    localStorage.removeItem("contingent.settings");
  }
  if(isEmpty(_settings) || !isArray(_settings))
  {
    localStorage.removeItem("contingent.settings");
    _settings = [defaultSettings];
  }
}

const initialState = {
  loading: { hierarchy: false },
  currentDay: DateTime.local(),
  settingsOpen: false,
  settings: _settings,
  months: "",
  ratePlans: [] as RatePlan[],
  contingentData: [] as ContingentUnitTypeView[],
};

const state = { ...initialState };

const getters: GetterTree<typeof state, any> = {
  isLoading: (s) => s.loading.hierarchy,
  currentDay: (s) => s.currentDay,
  settingsOpen: (s) => s.settingsOpen,
  settings: (s) => s.settings,
  ratePlans: (s) => s.ratePlans,
  months: (s) => s.months,
  contingentData: (s) => s.contingentData,
};

const actions: ActionTree<typeof state, any> = {
  setMonths: ({ commit }, payload: string) => {
    commit("setMonths", payload);
  },
  goToDay: ({ commit }, payload: DateTime) => {
    commit("setDay", payload);
  },
  openSettings: ({ commit }) => {
    commit("settingsMenu", true);
  },
  closeSettings: ({ commit }) => {
    commit("settingsMenu", false);
  },
  updateSettings: ({ commit }, payload: ContingentSettings) => {
    commit("updateSettings", payload);
  },
  refreshRatePlans({ commit }) {
    return Vue.axios
      .get<ListModel<RatePlan>>("/calendar/getListRatePlans")
      .then((res) => {
        commit("setRatePlans", res.data.result);
      });
  },
  fetchHierarchy: async (
    { commit },
    payload: ContingentDataRequest
  ): Promise<ContingentMonthView[]> => {
    commit("setLoadingHierarchy", true);

    try {
      const contingentDataPromise = Vue.axios.get<ContingentMonthView[]>(
        "ContigentOverview", {params: payload}
      );

      const [contingentData] = await Promise.all([
        contingentDataPromise,
      ]);

      const data = _.map(contingentData.data, 
        (m) => new ContingentMonthView (
          m.id,
          m.representationDate,
          true,
          _.map(m.unitTypes, 
            (ut, uti) => new ContingentUnitTypeView (
              ut.id,
              ut.name,
              true,
              payload.cell.h,
              payload.cell.h * uti,
              _.map(ut.daysData,
                (dd) => new ContingentDaysDataView (
                  dd.date,
                  dd.occupied,
                  dd.available,
                  dd.occupancyPercentage,
                  dd.demandData ? dd.demandData.map(
                    (ded) => new ContingentDemandDataView (
                      ded.name,
                      ded.statusId
                    )
                  ) : [],
                )
              ),
              _.has(ut, 'ratePlans') ? _.map(ut.ratePlans, 
                (rp) => new ContingentRatePlanView (
                  rp.id,
                  rp.name,
                  rp.currencyCode,
                  _.map(rp.ratePlanData,
                    (rpd) => new ContingentRatePlanDataView (
                      rpd.date,
                      rpd.initializedOnly,
                      rpd.price,
                      rpd.basePriceDifference,
                    )
                  ),
                  rp.isOBSpecific
                )
              ) : [],
            )
          ),
          payload.cell
        )
      );

      commit("setContingentData", data);
      return data;
    } finally {
      commit("setLoadingHierarchy", false);
    }
  },
};

const mutations: MutationTree<typeof state> = {
  reset: (state) => {
    Object.assign(state, initialState);
  },
  setMonths: (state, payload: string) => (state.months = payload),
  setDay: (state, payload: DateTime) => (state.currentDay = payload),
  settingsMenu: (state, payload: boolean) => (state.settingsOpen = payload),
  updateSettings: (state, payload: ContingentSettings) => {
    const x = state.settings?.findIndex(x => x.userId == payload.userId);
    if(x !== undefined && x !== -1)
    {
      state.settings?.splice(x, 1, payload);
    }
    else
    {
      state.settings?.push(payload);
    }
    localStorage.setItem("contingent.settings", JSON.stringify(state.settings));
  },
  setLoadingHierarchy: (state, payload: boolean) => {
    state.loading.hierarchy = payload;
  },
  setRatePlans: (state, value) => {
    state.ratePlans = value;
  },
  setContingentData: (state, value) => {
    state.contingentData = value;
  },
};

export default {
  namespaced: true,
  state: () => state,
  getters,
  actions,
  mutations,
};